import {
  useCreateOrUpdateArtworkInExhibition,
  useSyncItinerantExhibitionGroup,
} from "@/api/related-fields";
import { FormLayoutElement } from "@/config/forms/general";
import { ArtworkInExhibition } from "@/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import LinksDrawerLayout from "./LinksDrawerLayout";
import {
  ItinerantExhibitionPanel,
  ItinerantStopDraft,
  itinerantStopFromExhibition,
} from "./ItinerantExhibitionPanel";
import { ItinerantExhibitionPreview } from "./ItinerantExhibitionPreview";
import { ItinerantDrawerConfig } from "./itinerant-drawer-types";
import { useItinerantDrawerChrome } from "./useItinerantDrawerChrome";
import { useItinerantStopSelection } from "./useItinerantStopSelection";

const ArtworkInExhibitionDrawer = ({
  fields,
  data,
  queryId,
  type,
  open,
  onOpenChange,
  itinerantConfig,
}: {
  fields: FormLayoutElement[] | undefined;
  data: ArtworkInExhibition;
  queryId: string;
  type: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  itinerantConfig?: ItinerantDrawerConfig;
}) => {
  const schema = z.object({
    oeuvresDansExpositionsDescriptionNumeroCatalogue: z.string().optional(),
    oeuvresDansExpositionsNotes: z.string().optional(),
    exhibition: z.object({
      expositionsDescriptionNumeroExpo: z.string(),
      expositionsDescriptionResume: z.string(),
    }),
  });

  const { mutate: createOrUpdateArtworkInExhibition } =
    useCreateOrUpdateArtworkInExhibition(queryId, type);

  const { mutate: syncGroup, isPending: isSyncing } =
    useSyncItinerantExhibitionGroup(
      itinerantConfig?.parentId ?? "",
      itinerantConfig?.parentType ?? "artwork",
      itinerantConfig?.complexRelationKey,
    );

  const [stops, setStops] = useState<ItinerantStopDraft[]>([]);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      exhibition: data.exhibition,
      oeuvresDansExpositionsDescriptionNumeroCatalogue:
        data.oeuvresDansExpositionsDescriptionNumeroCatalogue,
      oeuvresDansExpositionsNotes: data.oeuvresDansExpositionsNotes,
    },
  });

  const { selectedStopId, selectStop, flushStopsForSubmit } =
    useItinerantStopSelection(itinerantConfig, stops, setStops, form, {
      catalogNumber: "oeuvresDansExpositionsDescriptionNumeroCatalogue",
      // Classic notes stay on the right form; dedicated itinerant note is per-stop on the left
    });

  const onSubmit = (formData: z.infer<typeof schema>) => {
    if (itinerantConfig) {
      const effectiveStops = flushStopsForSubmit();
      const stopsToSync =
        effectiveStops.length > 0
          ? effectiveStops
          : [
              itinerantStopFromExhibition(itinerantConfig.seedExhibition, undefined, {
                catalogNumber:
                  formData.oeuvresDansExpositionsDescriptionNumeroCatalogue ??
                  "",
              }),
            ];
      syncGroup({
        groupId: itinerantConfig.groupId ?? undefined,
        parentType: itinerantConfig.parentType,
        parentId: itinerantConfig.parentId,
        stops: stopsToSync.map((s, index) => ({
          linkId: s.linkId,
          exhibitionId: s.exhibition.id,
          catalogNumber: s.catalogNumber,
          exemplaireExpose: s.exemplaireExpose,
          note: s.note,
          order: index,
        })),
      });
      return;
    }

    createOrUpdateArtworkInExhibition({
      id: data.id ?? "",
      oeuvresDansExpositionsDescriptionNumeroCatalogue:
        formData.oeuvresDansExpositionsDescriptionNumeroCatalogue,
      oeuvresDansExpositionsNotes: formData.oeuvresDansExpositionsNotes,
      artworkId: data.artwork.id,
      exhibitionId: data.exhibition.id,
    });
  };
  const isFromExhibition = type === "exhibition";
  const previewConfig = isFromExhibition
    ? {
        attachments: data.artwork?.attachments ?? [],
        type: "artwork" as const,
        alt:
          data.artwork?.oeuvresDescriptionNumeroEnregistrement ??
          data.artwork?.oeuvresDescriptionTitre,
      }
    : {
        attachments: data.exhibition?.attachments ?? [],
        type: "exhibition" as const,
        alt:
          data.exhibition?.expositionsDescriptionNumeroExpo ??
          data.exhibition?.expositionsDescriptionTitre,
      };

  const sheetOpen = open ?? false;
  const { showItinerantSidebar, isItinerantContext, fieldsWithHint } =
    useItinerantDrawerChrome(itinerantConfig, fields);

  const catalogWatch = form.watch(
    "oeuvresDansExpositionsDescriptionNumeroCatalogue",
  );
  const previewStops = useMemo(
    () =>
      stops.map((stop) =>
        stop.id === selectedStopId
          ? { ...stop, catalogNumber: catalogWatch ?? stop.catalogNumber }
          : stop,
      ),
    [stops, selectedStopId, catalogWatch],
  );

  return (
    <LinksDrawerLayout
      form={form}
      fields={fieldsWithHint}
      recordId={data.id}
      onSubmit={onSubmit}
      previewImage={previewConfig}
      open={open}
      onOpenChange={onOpenChange}
      submitDisabled={isSyncing}
      formFooter={
        isItinerantContext ? (
          <ItinerantExhibitionPreview stops={previewStops} />
        ) : undefined
      }
      sidebar={
        showItinerantSidebar && itinerantConfig ? (
          <ItinerantExhibitionPanel
            open={sheetOpen}
            seedExhibition={itinerantConfig.seedExhibition}
            initialStops={itinerantConfig.initialStops}
            groupId={itinerantConfig.groupId}
            parentType={itinerantConfig.parentType}
            parentId={itinerantConfig.parentId}
            stops={stops}
            onStopsChange={setStops}
            inlineFields={false}
            selectedStopId={selectedStopId}
            onSelectStop={selectStop}
          />
        ) : undefined
      }
    />
  );
};

export default ArtworkInExhibitionDrawer;
